import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { AppLink, routeNaming } from '../../../routes';
import { constants } from '../../../config';
import { ProductImage } from '../../../common/product-image';
import { Select } from '../../../common/select';
import { ProductHelper } from '../../../helpers/ProductHelper';
import { CART_ITEM_STATES } from '../../../helpers/CartHelper';
import { CartStore } from '../../../stores/CartStore';
import { ScreenStore } from '../../../stores/ScreenStore';
import iconVerticalDots from './icon-vertical-dots.svg';
import styles from './TableCard.css';
import { InfoModal } from '../../../common/info-modal';
import { GA4 } from '../../../helpers/GA4';

const IProduct = PropTypes.shape({
  id: PropTypes.string.isRequired,
  coverImage: PropTypes.string,
  brand: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  financing: PropTypes.shape({
    priceString: PropTypes.string.isRequired,
  }).isRequired,
  maxAmountToSell: PropTypes.number.isRequired,
  serviceProduct: PropTypes.object,
  stockIsEmpty: PropTypes.func.isRequired,
  stockIsAlmostNone: PropTypes.func.isRequired,
  stockIsNearlyEmpty: PropTypes.func.isRequired,
});

const IPropTypes = {
  className: PropTypes.string,
  hasService: PropTypes.bool,
  isCartItem: PropTypes.bool.isRequired,
  maxAmount: PropTypes.number,
  item: PropTypes.oneOfType([
    IProduct,
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      product: IProduct,
    }).isRequired,
  ]).isRequired,
};

const defaultProps = {
  className: '',
  hasService: false,
  maxAmount: 0,
};

const cartAmountCallback = (item, hasService) => value => (
  CartStore.changeCartProductAmount(
    item.product.id,
    hasService ? value + (item.amount - item.serviceAmount) : value + item.serviceAmount,
    hasService ? value : item.serviceAmount,
    false,
  )
);

const addInstallationActionCallback = (productId) => {
  CartStore.addInstallationFromProduct(productId);
};

const removeInstallationActionCallback = (productId) => {
  CartStore.removeInstallationFromProduct(productId);
};

const onDeleteActionCallback = (props) => {
  if (
    props.isCartItem &&
    props.hasService &&
    props.item.product.serviceProduct &&
    props.item.amount > props.item.serviceAmount
  ) {
    /*
      The item is in cart, is the service version, has a service product
      and not all of the items have service checked.
    */
    return () => CartStore.changeCartProductAmount(
      props.item.product.id,
      props.item.amount - props.item.serviceAmount,
      0,
      false,
      true,
    );
  } else if (
    props.isCartItem &&
    !props.hasService &&
    props.item.product.serviceProduct &&
    props.item.serviceAmount > 0
  ) {
    /*
      The item is in cart, isn't the service version, has a service
      product and service amount isn't zero.
    */
    return () => CartStore.changeCartProductAmount(
      props.item.product.id,
      props.item.serviceAmount,
      props.item.serviceAmount,
      false,
      true,
    );
  } else if (!props.isCartItem) {
    return () => CartStore.deleteProductFromFavorites(props.item.id);
  }
  return () => {
    GA4.removeFromCartEvent(props.item.product.financing && props.item.product.financing.price, {
      item_id: props.item.product.id,
      item_name: props.item.product.name.trim(),
      price: props.item.product.financing && props.item.product.financing.price,
    });
    CartStore.deleteProductFromCart(props.item.product.id);
  };
};

const renderStatus = (status) => {
  switch (status) {
    case CART_ITEM_STATES.NO_STOCK:
      return 'No hay más stock de este producto';
    case CART_ITEM_STATES.MAX_AMOUNT_CHANGED:
      return 'La cantidad máxima aceptada del producto ha cambiado';
    case CART_ITEM_STATES.PRICE_CHANGED:
      return 'El precio de este producto ha cambiado';
    case CART_ITEM_STATES.PRODUCT_STATE_CHANGED:
      return 'Este producto ya no puede ser incluido en el carrito';
    default:
      return '';
  }
};

const ControlInstallation = (props) => {
  if (props.product.serviceProduct) {
    return props.hasService ?
      <button
        className={styles.actionButton}
        onClick={() => removeInstallationActionCallback(props.product.id)}
      >
        Quitar instalación
      </button> :
      <button
        className={styles.actionButton}
        onClick={() => addInstallationActionCallback(props.product.id)}
      >
        Agregar instalación
      </button>;
  }
  return null;
};

ControlInstallation.propTypes = {
  hasService: PropTypes.bool,
  product: PropTypes.any,
};

ControlInstallation.defaultProps = {
  hasService: true,
  product: {},
};

const TableCard = (props) => {
  const mobileVersion = ScreenStore.screenWidth <= constants.breakpoints.extraSmallMaxWidth;
  const product = props.isCartItem ? props.item.product : props.item;
  const isNotAvailableInCart = props.isCartItem &&
    props.item.state === CART_ITEM_STATES.PRODUCT_STATE_CHANGED;
  let stockLevelMessage = 'Stock disponible';
  if (product.stockIsEmpty()) {
    const piece = product.notAbleToSellString && product.notAbleToSellString.split(' disponible ');
    stockLevelMessage = `Sin stock. Fecha estimada: ${piece && piece.length > 1 ? piece[1] : '-'}.`;
    if (!product.shouldShowPrice()) {
      stockLevelMessage += 'Fijado el precio se publicará inmediatamente.';
    }
  } else if (product.stockIsAlmostNone()) {
    stockLevelMessage = 'No hay, o hay muy poco';
  } else if (product.stockIsNearlyEmpty()) {
    stockLevelMessage = 'Stock disponible, últimas unidades';
  }
  let onMoveAction = () => CartStore.moveProductFromCartToList(product.id, props.hasService);
  if (!props.isCartItem) {
    onMoveAction = () => CartStore.moveProductFromListToCart(product.id);
  }
  const onDeleteAction = onDeleteActionCallback(props);
  const priceContainer = (
    <div className={styles.priceContainer}>
      {
        product.shouldShowPrice() ?
          `$${ProductHelper.priceStringFor(product, props.hasService)}` :
          'Precio sin determinar'
      }
    </div>
  );
  const itemCanNotBeMovedFromListToCart = (
    !props.isCartItem && (!product.ableToSell || product.stockIsEmpty())
  ) || isNotAvailableInCart;
  const moveActionText = props.isCartItem ? 'Mover a favoritos' : 'Mover al carrito';
  const mobileSelectOptions = !itemCanNotBeMovedFromListToCart ? [
    {
      label: moveActionText,
      value: onMoveAction,
    },
    {
      label: 'Borrar',
      value: onDeleteAction,
    },
  ] :
    [
      {
        label: 'Borrar',
        value: onDeleteAction,
      },
    ];

  if (product.serviceProduct) {
    const moreProps = {
      label: props.hasService ? 'Quitar instalación' : 'Agregar instalación',
      value: props.hasService ? () => removeInstallationActionCallback(product.id) :
        () => addInstallationActionCallback(product.id),
    };
    mobileSelectOptions.splice(1, 0, moreProps);
  }

  const productAmountOptions = [];
  for (let i = 1; i <= props.maxAmount; i += 1) {
    productAmountOptions.push({
      label: i,
      value: i,
    });
  }

  const amountSelect = (
    props.isCartItem
      ? (
        <Select
          className={styles.amountSelect}
          onChange={cartAmountCallback(props.item, props.hasService)}
          optionClassName={styles.amount}
          options={productAmountOptions}
          selectedClassName={styles.amount}
          value={`${mobileVersion ? 'Cant. ' : ''}${props.hasService ? props.item.serviceAmount : (props.item.amount - props.item.serviceAmount)}`}
        />
      )
      : null
  );
  return (
    <div className={[styles.container, props.className].join(' ')}>
      <div className={styles.productData}>
        <div className={styles.imageContainer}>
          {
            isNotAvailableInCart ?
              <ProductImage
                className={mobileVersion ? styles.imageMobile : styles.image}
                src={product.coverImage}
                size={mobileVersion ? 'sm' : 'md'}
              /> :
                <AppLink
                  routeName={routeNaming.PRODUCT_DETAIL}
                  pathParams={{ id: product.id }}
                  className={styles.imageLink}
                >
                  <ProductImage
                    className={mobileVersion ? styles.imageMobile : styles.image}
                    src={product.coverImage}
                    size={mobileVersion ? 'sm' : 'md'}
                  />
                </AppLink>
          }
        </div>
        <div>
          <p className={styles.brand}>
            {product.brand}
          </p>
          {
            isNotAvailableInCart ?
              <h3 className={styles.name}>
                {product.name}
              </h3> :
              <AppLink
                routeName={routeNaming.PRODUCT_DETAIL}
                pathParams={{ id: product.id }}
              >
                <h3 className={styles.name}>
                  {product.name}
                </h3>
              </AppLink>
          }
          {
            mobileVersion &&
            priceContainer
          }
          <p className={styles.stockLevel}>
            {stockLevelMessage}
          </p>
          {
            !!product.serviceProduct &&
            props.hasService &&
            <InfoModal
              id="servicio-instalación-covid"
              type="component"
              showConfirm={false}
              textCancel="Cerrar"
              showModal={constants.activeCovid}
            >
              <p className={styles.hasServiceMessage}>
                Incluye instalación de equipo
              </p>
            </InfoModal>
          }
          {
            !!product.serviceProduct &&
            !props.hasService &&
            <p className={styles.noServiceMessage}>
              No incluye instalación de equipo
            </p>
          }
          {
            Object.values(CART_ITEM_STATES).includes(props.item.state) &&
            props.item.state !== CART_ITEM_STATES.NO_ISSUES &&
            <p className={styles.cartErrorMessage}>
              {renderStatus(props.item.state)}
            </p>
          }
          {
            mobileVersion &&
            <div className={props.isCartItem ? styles.mobileActionContainer :
              styles.mobileActionContainerFavourites}
            >
              {
                props.isCartItem &&
                amountSelect
              }
              <Select
                dropdownClassName={styles.mobileActionDropdown}
                noIndicator
                onChange={callback => callback()}
                options={mobileSelectOptions}
                rightAligned
                value={<img alt="" src={iconVerticalDots} />}
              />
            </div>
          }
        </div>
      </div>
      {
        !mobileVersion &&
        priceContainer
      }
      {
        !mobileVersion &&
        <div className={styles.actionSection}>
          {
            props.isCartItem &&
            amountSelect
          }
          {!itemCanNotBeMovedFromListToCart &&
            <button
              className={styles.actionButton}
              onClick={onMoveAction}
            >
              {moveActionText}
            </button>
          }
          <ControlInstallation product={product} hasService={props.hasService} />
          <button
            className={styles.actionButton}
            onClick={onDeleteAction}
          >
            Borrar
          </button>
        </div>
      }
    </div>
  );
};

TableCard.propTypes = IPropTypes;
TableCard.defaultProps = defaultProps;

const ObservedTableCard = observer(TableCard);

export { ObservedTableCard as TableCard };



// WEBPACK FOOTER //
// ./src/pages/cart/table-card/TableCard.jsx